import { useState } from 'react';
import { ArrowDown, ArrowUp, Lightbulb, Minus, Star } from 'lucide-react';
import type { CsAiChatAnalysis, CsAiInsight } from '../../types/cs-inquiry.js';

/** @graph-connects none */
const PRIORITY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  high: { bg: 'bg-red-50', text: 'text-red-700', label: '高' },
  medium: { bg: 'bg-yellow-50', text: 'text-yellow-700', label: '中' },
  low: { bg: 'bg-blue-50', text: 'text-blue-700', label: '低' },
};

/** @graph-connects none */
function SentimentIcon({ sentiment }: { sentiment: string }) {
  if (sentiment === 'positive') return <ArrowUp className="h-4 w-4 text-green-500" />;
  if (sentiment === 'negative') return <ArrowDown className="h-4 w-4 text-red-500" />;
  return <Minus className="h-4 w-4 text-gray-400" />;
}

/** @graph-connects none */
function QualityStars({ score }: { score: number }) {
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`}
        />
      ))}
    </span>
  );
}

/** @graph-connects none */
export function InsightCard({ insight }: { insight: CsAiInsight }) {
  const style = PRIORITY_STYLES[insight.priority] ?? PRIORITY_STYLES['low'];
  return (
    <div className={`rounded-lg border p-4 ${style.bg}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className={`h-4 w-4 ${style.text}`} />
          <span className="font-medium text-gray-900">{insight.title}</span>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
          {style.label}
        </span>
      </div>
      <p className="text-sm text-gray-600">{insight.description}</p>
      <span className="mt-2 inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
        {insight.category}
      </span>
    </div>
  );
}

/** @graph-connects none */
export function ChatAnalysisRow({ chat }: { chat: CsAiChatAnalysis }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <tr
      className="cursor-pointer border-b hover:bg-gray-50"
      onClick={() => {
        setExpanded(!expanded);
      }}
    >
      <td className="px-3 py-2 text-sm">
        <div className="flex items-center gap-1.5">
          <SentimentIcon sentiment={chat.sentiment} />
          <span className="max-w-[200px] truncate">{chat.summary}</span>
        </div>
        {expanded && chat.responseQualityNotes.length > 0 && (
          <ul className="mt-2 list-inside list-disc text-xs text-gray-500">
            {chat.responseQualityNotes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-3 py-2 text-sm">{chat.category}</td>
      <td className="px-3 py-2 text-sm text-gray-500">{chat.subcategory}</td>
      <td className="px-3 py-2 text-center">
        <QualityStars score={chat.responseQualityScore} />
      </td>
      <td className="px-3 py-2 text-center">
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${
            chat.urgency === 'high'
              ? 'bg-red-100 text-red-700'
              : chat.urgency === 'medium'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-gray-100 text-gray-600'
          }`}
        >
          {chat.urgency === 'high' ? '高' : chat.urgency === 'medium' ? '中' : '低'}
        </span>
      </td>
    </tr>
  );
}
